import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Star, Zap, CalendarClock, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn, getInitials } from '@/lib/utils';
import {
  useSessionAttendance,
  useMarkAttendance,
  useCancelMakeup,
} from '@/hooks/use-attendance';
import { EvaluationModal } from '@/components/coach/evaluation-modal';
import { BulkEvaluationWizard } from '@/components/coach/bulk-evaluation-wizard';
import type { Player } from '@/shared/types/coach.types';
import { SessionNotes } from '@/components/coach/session-notes';
import { SessionEvaluationsSummary } from '@/components/coach/session-evaluations-summary';
import { useSessionEvaluations } from '@/hooks/use-evaluation';
import { PlayerGoals } from '@/components/coach/player-goals';
import { DrillLog } from './drill-log';
import { useCoachDrillSuggestions } from '@/hooks/coach/use-coach-sessions';

// ─── Private Session View ──────────────────────────────────────────────────────

interface PrivateSessionViewProps {
  sessionId: string;
}

export function PrivateSessionView({ sessionId }: PrivateSessionViewProps) {
  const navigate = useNavigate();
  const { data } = useSessionAttendance(sessionId);
  const { data: sessionEvaluations } = useSessionEvaluations(sessionId);
  const markAttendance = useMarkAttendance(sessionId);
  const cancelMakeup = useCancelMakeup(sessionId);
  const drillSuggestions = useCoachDrillSuggestions();

  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [evalModalOpen, setEvalModalOpen] = useState(false);
  const [bulkEvalOpen, setBulkEvalOpen] = useState(false);

  // For private sessions, the primary player is the first (and usually only) one
  const primaryPlayer = data?.players[0] ?? null;

  const playersToEvaluate = useMemo(() => {
    if (!data) return [];
    return data.players
      .filter(p => (p.status === 'present' || p.status === 'late') && !p.hasEvaluation)
      .map(p => ({ id: p.id, firstName: p.firstName, lastName: p.lastName, level: p.level }));
  }, [data]);

  const handleOpenEvaluation = (player: {
    id: string;
    firstName: string;
    lastName: string;
    level: string | null;
  }, e: React.MouseEvent) => {
    e.stopPropagation();
    const skillLevel = (player.level || 'beginner') as 'beginner' | 'intermediate' | 'advanced' | 'elite';
    setSelectedPlayer({
      id: player.id,
      firstName: player.firstName,
      lastName: player.lastName,
      dateOfBirth: '',
      skillLevel,
      ballLevel: null,
      utrRating: null,
      photoUrl: null,
    });
    setEvalModalOpen(true);
  };

  const handleToggleAttendance = (playerId: string, currentStatus: string) => {
    let newStatus: 'present' | 'late' | 'absent';
    if (currentStatus === 'present') newStatus = 'late';
    else if (currentStatus === 'late') newStatus = 'absent';
    else newStatus = 'present';
    markAttendance.mutate({ playerId, status: newStatus });
  };

  const handleRequestMakeup = (playerId: string, hasMakeupRequest: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasMakeupRequest) {
      cancelMakeup.mutate({ playerId });
    } else {
      markAttendance.mutate({ playerId, status: 'absent', makeupRequired: true });
    }
  };

  if (!data) return null;

  return (
    <>
      {/* Player Focus Card */}
      {data.players.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <User className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No player added to this session yet.</p>
            <p className="text-xs text-muted-foreground mt-1">Add a player from the session list.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <User className="w-4 h-4" />
              Player
            </CardTitle>
            <CardDescription>Tap to cycle attendance status</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.players.map(player => (
              <div
                key={player.id}
                onClick={() => handleToggleAttendance(player.id, player.status)}
                className={cn(
                  'flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition',
                  player.status === 'present'
                    ? 'border-green-500 bg-green-50 dark:bg-green-950/30'
                    : player.status === 'late'
                    ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/30'
                    : player.status === 'absent'
                    ? 'border-red-300 bg-red-50 dark:bg-red-950/30'
                    : 'border-border bg-card hover:bg-muted/40'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-base">
                    {getInitials(player.firstName, player.lastName)}
                  </div>
                  <div>
                    <div className="font-semibold text-foreground text-base">
                      {player.firstName} {player.lastName}
                    </div>
                    <div className="text-sm text-muted-foreground capitalize">
                      {player.level || 'Unassigned'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(player.status === 'present' || player.status === 'late') && (
                    <Button
                      size="sm"
                      variant={player.hasEvaluation ? 'outline' : 'default'}
                      className={cn(
                        'h-8 px-2',
                        player.hasEvaluation ? 'border-green-500 text-green-600 hover:bg-green-50' : ''
                      )}
                      onClick={(e) => handleOpenEvaluation(player, e)}
                    >
                      {player.hasEvaluation ? (
                        <><CheckCircle className="w-4 h-4 mr-1" /><span className="text-xs">Done</span></>
                      ) : (
                        <><Star className="w-4 h-4 mr-1" /><span className="text-xs">Eval</span></>
                      )}
                    </Button>
                  )}
                  {player.status === 'absent' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className={cn(
                        'h-8 px-2',
                        player.hasMakeupRequest
                          ? 'border-green-500 text-green-600 hover:bg-green-50'
                          : 'border-orange-400 text-orange-600 hover:bg-orange-50'
                      )}
                      onClick={(e) => handleRequestMakeup(player.id, player.hasMakeupRequest, e)}
                    >
                      {player.hasMakeupRequest ? (
                        <><CheckCircle className="w-4 h-4 mr-1" /><span className="text-xs">Makeup Requested</span></>
                      ) : (
                        <><CalendarClock className="w-4 h-4 mr-1" /><span className="text-xs">Makeup</span></>
                      )}
                    </Button>
                  )}
                  <div className={cn(
                    'w-8 h-8 rounded-full flex items-center justify-center transition',
                    player.status === 'present' ? 'bg-green-500'
                      : player.status === 'late' ? 'bg-yellow-500'
                      : player.status === 'absent' ? 'bg-red-400'
                      : 'bg-muted'
                  )}>
                    {player.status === 'present' && <CheckCircle className="w-5 h-5 text-white" />}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Evaluations Summary */}
      <SessionEvaluationsSummary sessionId={sessionId} presentCount={data.presentCount} />

      {/* Player Goals — scoped to the primary player */}
      {primaryPlayer && (
        <PlayerGoals playerId={primaryPlayer.id} />
      )}

      {/* Drill Log */}
      <DrillLog sessionId={sessionId} drills={data.session.drillLog} suggestions={drillSuggestions} />

      {/* Session Notes */}
      <SessionNotes sessionId={sessionId} />

      {/* Action Buttons */}
      {playersToEvaluate.length > 0 && (
        <Button onClick={() => setBulkEvalOpen(true)} className="w-full">
          <Zap className="w-4 h-4 mr-2" />
          Start Evaluations ({playersToEvaluate.length} players)
        </Button>
      )}

      <Button variant="outline" onClick={() => navigate(-1)} className="w-full">
        Done
      </Button>

      {/* Evaluation Modal */}
      <EvaluationModal
        player={selectedPlayer}
        sessionId={sessionId}
        open={evalModalOpen}
        onOpenChange={setEvalModalOpen}
        onSuccess={() => {}}
        initialEvaluation={(sessionEvaluations ?? []).find(e => e.playerId === selectedPlayer?.id)}
      />

      {/* Bulk Evaluation Wizard */}
      <BulkEvaluationWizard
        sessionId={sessionId}
        players={playersToEvaluate}
        open={bulkEvalOpen}
        onOpenChange={setBulkEvalOpen}
        onComplete={() => {}}
      />
    </>
  );
}
